import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scanFeedback } from "@/lib/feedback";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Camera, CameraOff, CheckCircle2, XCircle, ArrowDownToLine, ArrowUpFromLine, Trash2, Boxes } from "lucide-react";

const stockIdFrom = (text) => { const m = String(text).match(/\/stock\/scan\/([0-9a-f-]{36})/i) || String(text).match(/^(STK-\d{4,})$/i); return m ? m[1] : null; };
const REGION = "stock-bulk-reader";

export default function StockBulkScan() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("type") === "out" ? "out" : "in");
  const [qty, setQty] = useState("1");
  const [job, setJob] = useState("");
  const [note, setNote] = useState("");
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const scanner = useRef(null);
  const lastSeen = useRef({});
  const busy = useRef(false);
  const opts = useRef({});
  opts.current = { type, qty, job, note };

  const push = (e) => setLog((l) => [{ ...e, at: new Date() }, ...l].slice(0, 100));

  const handle = async (text) => {
    const id = stockIdFrom(text);
    const now = Date.now();
    if (!id || busy.current || now - (lastSeen.current[id] || 0) < 2500) return;
    lastSeen.current[id] = now; busy.current = true;
    const { type, qty, job, note } = opts.current;
    try {
      const q = Number(qty);
      if (!(q > 0)) { scanFeedback("error"); push({ kind: "err", name: id, msg: "Set quantity per scan first" }); return; }
      const { data } = await api.post(`/stock/${id}/move`, { type, quantity: q, job_number: job, note: note || `Camera bulk ${type}` });
      scanFeedback("success");
      push({ kind: "ok", id: data.item.id, sku: data.item.sku, name: data.item.name, qty: data.entry.quantity, balance: data.item.quantity, unit: data.item.unit, low: data.item.low });
    } catch (e) {
      scanFeedback("error");
      const d = e?.response?.data?.detail;
      push({ kind: "err", name: id, msg: typeof d === "string" ? d : e?.response?.status === 404 ? "Stock item not found" : "Update failed" });
    } finally { busy.current = false; }
  };

  const start = async () => {
    try {
      scanner.current = scanner.current || new Html5Qrcode(REGION, { verbose: false });
      await scanner.current.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, handle, () => {});
      setRunning(true);
    } catch { push({ kind: "err", name: "Camera", msg: "Camera not available. Allow camera access and try again." }); }
  };
  const stop = async () => { try { await scanner.current?.stop(); } catch { /* scanner already stopped */ } setRunning(false); };
  useEffect(() => () => { scanner.current?.stop().catch(() => {}); }, []);
  useEffect(() => { window.__stockScanHandle = handle; return () => { delete window.__stockScanHandle; }; });

  const total = log.filter((l) => l.kind === "ok").reduce((s, l) => s + Math.abs(l.qty), 0);
  const isIn = type === "in";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4" data-testid="stock-bulk-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3"><img src="/rozfa-logo-white.png" alt="Rozfa" className="h-8 w-auto" /><div className="text-xs font-mono text-slate-400 uppercase tracking-widest">Stock Camera Scan</div></div>
          <div className="text-xs text-slate-400">{user?.name}</div>
        </div>

        <Card className="p-3 bg-white text-slate-900 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setType("in")} className={`h-12 rounded-lg font-bold flex items-center justify-center gap-2 border-2 ${isIn ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-600 text-emerald-700"}`} data-testid="stock-bulk-type-in"><ArrowDownToLine className="w-5 h-5" /> Stock In</button>
            <button type="button" onClick={() => setType("out")} className={`h-12 rounded-lg font-bold flex items-center justify-center gap-2 border-2 ${!isIn ? "bg-rose-600 text-white border-rose-600" : "border-rose-600 text-rose-700"}`} data-testid="stock-bulk-type-out"><ArrowUpFromLine className="w-5 h-5" /> Stock Out</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Qty per scan *</Label><Input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="h-11 text-lg font-mono" data-testid="stock-bulk-qty" /></div>
            <div className="col-span-2"><Label className="text-xs">Reference (DO / PO / Job No.)</Label><Input value={job} onChange={(e) => setJob(e.target.value.toUpperCase())} placeholder="DO-12345 / RZF-…" className="h-11" data-testid="stock-bulk-ref" /></div>
          </div>
          <div className="flex gap-1 flex-wrap">{[1, 5, 10, 50, 100].map((n) => <button key={n} type="button" onClick={() => setQty(String(n))} className={`px-3 py-1 rounded-full border text-xs font-mono ${qty === String(n) ? "bg-slate-900 text-white" : "hover:bg-muted"}`} data-testid={`stock-bulk-qty-${n}`}>{n}</button>)}</div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-9 text-sm" data-testid="stock-bulk-note" />
          <div className="text-xs text-muted-foreground">Every stock QR scanned will record <span className={`font-bold ${isIn ? "text-emerald-700" : "text-rose-700"}`}>{isIn ? "+" : "−"}{qty || 0}</span> immediately. Scan the same label again to add another {qty || 0}.</div>
        </Card>

        <div className="rounded-xl overflow-hidden bg-black border-2 border-slate-700 relative" style={{ minHeight: running ? 0 : 200 }}>
          <div id={REGION} data-testid="stock-bulk-reader" />
          {!running && <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">Camera off</div>}
        </div>
        <Button onClick={running ? stop : start} className={`w-full h-12 font-semibold text-white ${running ? "bg-rose-600 hover:bg-rose-700" : isIn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="stock-bulk-toggle">
          {running ? <><CameraOff className="w-4 h-4 mr-2" /> Stop Camera</> : <><Camera className="w-4 h-4 mr-2" /> Start Camera</>}
        </Button>

        <div className="flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Session <Badge variant="secondary" className="ml-1 font-mono" data-testid="stock-bulk-count">{log.filter((l) => l.kind === "ok").length} scans • {total} units</Badge></div>
          {log.length > 0 && <button onClick={() => setLog([])} className="text-xs text-slate-400 flex items-center gap-1 hover:text-white" data-testid="stock-bulk-clear"><Trash2 className="w-3 h-3" /> Clear</button>}
        </div>
        <div className="space-y-2" data-testid="stock-bulk-log">
          {log.length === 0 && <Card className="p-6 bg-slate-900 border-slate-800 text-center text-slate-500 text-sm">Set the quantity per scan, start the camera and scan each incoming carton / roll / ream QR label.</Card>}
          {log.map((l, i) => (
            <button key={i} onClick={() => l.id && nav(`/stock/scan/${l.id}`)} data-testid={`stock-bulk-entry-${l.sku || i}`}
              className={`w-full text-left rounded-lg p-3 flex items-center gap-3 border ${l.kind === "ok" ? (l.low ? "bg-amber-950/60 border-amber-700" : "bg-emerald-950/60 border-emerald-700") : "bg-rose-950/60 border-rose-700"}`}>
              {l.kind === "ok" ? <CheckCircle2 className={`w-5 h-5 shrink-0 ${l.low ? "text-amber-400" : "text-emerald-400"}`} /> : <XCircle className="w-5 h-5 text-rose-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2"><span className="font-mono text-xs text-slate-400">{l.sku}</span>{l.name}</div>
                <div className="text-xs text-slate-300">{l.kind === "ok" ? <>{l.qty > 0 ? "+" : ""}{l.qty} {l.unit} → balance <span className="font-mono font-bold">{l.balance}</span>{l.low && " • LOW STOCK"}</> : l.msg}</div>
              </div>
              <div className="text-[10px] font-mono text-slate-400">{l.at.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
            </button>
          ))}
        </div>
        <Button variant="ghost" onClick={() => nav("/stock")} className="w-full text-slate-400" data-testid="stock-bulk-back"><Boxes className="w-4 h-4 mr-2" /> Back to Stock list</Button>
      </div>
    </div>
  );
}
